import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, ScanLine, Fuel, UtensilsCrossed, Package, Smartphone, RotateCcw, MousePointerClick, ArrowRight } from "lucide-react";

const SAMPLES = [
  { id: "gas", icon: Fuel, vendor: "Shell Gas Station", amount: 52.4, cat: "Car & truck", tone: "sky", date: "Jun 12, 2026" },
  { id: "meal", icon: UtensilsCrossed, vendor: "Chipotle", amount: 14.85, cat: "Meals", tone: "rose", date: "Jun 14, 2026" },
  { id: "office", icon: Package, vendor: "Staples", amount: 28.99, cat: "Office expense", tone: "amber", date: "Jun 16, 2026" },
  { id: "phone", icon: Smartphone, vendor: "Verizon Wireless", amount: 41.0, cat: "Utilities", tone: "emerald", date: "Jun 18, 2026" },
];

const TONES = {
  sky: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  rose: "bg-rose-400/10 text-rose-300 border-rose-400/20",
  amber: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  emerald: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
};

function useCountUp(target) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    const start = val;
    const startTime = performance.now();
    const dur = 650;
    const tick = (now) => {
      const p = Math.min(1, (now - startTime) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(start + (target - start) * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return val;
}

export default function InteractiveDemo({ onStart }) {
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(null);
  const [used, setUsed] = useState({});
  const timer = useRef();

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const animatedTotal = useCountUp(total);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handlePick = (sample) => {
    if (scanning || used[sample.id]) return;
    setScanning(sample);
    timer.current = setTimeout(() => {
      setRows((prev) => [{ ...sample, key: Date.now() }, ...prev]);
      setUsed((u) => ({ ...u, [sample.id]: true }));
      setScanning(null);
    }, 1500);
  };

  const reset = () => {
    clearTimeout(timer.current);
    setRows([]);
    setUsed({});
    setScanning(null);
  };

  const allDone = rows.length === SAMPLES.length;

  return (
    <div data-testid="interactive-demo" className="w-full">
      {/* App frame */}
      <div className="relative w-full aspect-[4/3] bg-[#0A0A0A] border border-white/10 rounded-3xl shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] overflow-hidden flex flex-col">
        <div className="flex items-center gap-2 px-5 h-11 border-b border-white/5 bg-white/[0.02] shrink-0">
          <span className="h-3 w-3 rounded-full bg-white/15" />
          <span className="h-3 w-3 rounded-full bg-white/15" />
          <span className="h-3 w-3 rounded-full bg-white/15" />
          <div className="flex items-center gap-1.5 ml-3">
            <img src="/brand-icon-gold.png" alt="" className="h-4 w-4 object-contain rounded" />
            <span className="text-xs font-bold tracking-tight text-zinc-100">Receipt Republic</span>
          </div>
          <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#D4C3A3] bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 px-2 py-0.5 rounded-full">Live demo</span>
        </div>

        <div className="flex-1 p-5 grid grid-rows-[auto_1fr] gap-4 min-h-0">
          <div className="rounded-2xl bg-gradient-to-br from-[#D4C3A3]/15 to-transparent border border-[#D4C3A3]/15 text-white p-4 flex items-center justify-between relative overflow-hidden">
            <motion.div
              key={rows.length}
              initial={{ opacity: 0.4, scale: 0.9 }}
              animate={{ opacity: 0, scale: 2.4 }}
              transition={{ duration: 0.7 }}
              className="absolute -left-6 -top-6 h-24 w-24 rounded-full bg-[#D4C3A3]/25"
            />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Deductions so far</p>
              <p data-testid="demo-total" className="font-mono-num text-3xl font-bold tracking-tight text-[#E5D6B8]">${animatedTotal.toFixed(2)}</p>
              <p className="text-[11px] text-zinc-500 font-semibold mt-0.5">≈ ${(animatedTotal * 0.25).toFixed(2)} saved in taxes</p>
            </div>
            <div className="text-right relative">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Receipts</p>
              <p className="font-mono-num text-2xl font-bold text-zinc-100">{rows.length}</p>
            </div>
          </div>

          <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden min-h-0">
            <div className="grid grid-cols-[1fr_auto] px-4 py-2.5 border-b border-white/5 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
              <span>Vendor · Category</span><span>Amount</span>
            </div>
            <div className="divide-y divide-white/5">
              <AnimatePresence initial={false}>
                {rows.map((r) => (
                  <motion.div key={r.key}
                    initial={{ opacity: 0, y: -8, backgroundColor: "rgba(212,195,163,0.10)" }}
                    animate={{ opacity: 1, y: 0, backgroundColor: "rgba(255,255,255,0)" }}
                    transition={{ duration: 0.5 }}
                    className="grid grid-cols-[1fr_auto] items-center px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-100 truncate">{r.vendor}</p>
                      <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${TONES[r.tone]}`}>{r.cat}</span>
                    </div>
                    <span className="text-sm font-bold text-zinc-50 font-mono-num">${r.amount.toFixed(2)}</span>
                  </motion.div>
                ))}
              </AnimatePresence>
              {rows.length === 0 && !scanning && (
                <div className="px-4 py-10 text-center">
                  <MousePointerClick className="h-6 w-6 mx-auto text-[#D4C3A3]/70 mb-2" />
                  <p className="text-xs text-zinc-500 font-medium">Tap a receipt below to watch the AI sort it →</p>
                </div>
              )}
            </div>

            <AnimatePresence>
              {scanning && (
                <motion.div
                  initial={{ opacity: 0, y: 60, rotate: 6, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, rotate: -2, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.6, y: -20 }}
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  className="absolute bottom-4 right-4 w-32 bg-[#0f0f0f] rounded-lg shadow-2xl border border-white/10 p-3 overflow-hidden">
                  <div className="flex items-center gap-1 mb-2">
                    <Camera className="h-3 w-3 text-zinc-500" />
                    <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider">Reading…</span>
                  </div>
                  <p className="text-[10px] font-bold text-zinc-200 truncate">{scanning.vendor}</p>
                  <div className="mt-1 space-y-1">
                    <div className="h-1.5 w-full bg-white/10 rounded" />
                    <div className="h-1.5 w-2/3 bg-white/10 rounded" />
                    <div className="h-1.5 w-4/5 bg-white/10 rounded" />
                  </div>
                  <p className="mt-2 text-xs font-bold text-[#E5D6B8] font-mono-num">${scanning.amount.toFixed(2)}</p>
                  <motion.div initial={{ top: "-10%" }} animate={{ top: "110%" }}
                    transition={{ duration: 1.1, ease: "linear", repeat: Infinity }}
                    className="absolute left-0 right-0 h-8 bg-gradient-to-b from-transparent via-[#D4C3A3]/50 to-transparent shadow-[0_0_20px_rgba(212,195,163,0.5)]" />
                  <div className="absolute top-1.5 right-1.5 flex items-center gap-1 bg-[#D4C3A3] text-black text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                    <ScanLine className="h-2.5 w-2.5" /> AI
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Interactive receipt chips */}
      <div className="mt-5">
        <AnimatePresence>
          {allDone && (
            <motion.div
              initial={{ opacity: 0, y: 10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              className="mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-[#D4C3A3]/25 bg-[#D4C3A3]/[0.06] p-4"
            >
              <p className="text-sm font-semibold text-zinc-300">
                4 receipts, sorted in seconds — <span className="text-[#E5D6B8] font-mono-num">${total.toFixed(2)}</span> in deductions found.
              </p>
              {onStart && (
                <button
                  data-testid="demo-cta"
                  onClick={onStart}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#D4C3A3] text-black text-sm font-semibold px-5 py-2.5 hover:bg-[#E5D6B8] hover:scale-[1.03] transition-all duration-300"
                >
                  Do this for real — free <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">
            {allDone ? "Nice — that's how easy it is." : "Try it — tap a receipt"}
          </p>
          {rows.length > 0 && (
            <button data-testid="demo-reset" onClick={reset} className="flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-[#D4C3A3] transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {SAMPLES.map((s, i) => {
            const done = used[s.id];
            const busy = scanning?.id === s.id;
            return (
              <button
                key={s.id}
                data-testid={`demo-receipt-chip-${s.id}`}
                onClick={() => handlePick(s)}
                disabled={done || !!scanning}
                className={`group relative flex flex-col items-start gap-1.5 rounded-2xl border p-3 text-left transition-all duration-200
                  ${done ? "border-emerald-400/30 bg-emerald-400/[0.06] opacity-70" : "border-white/10 bg-white/[0.03] hover:border-[#D4C3A3]/50 hover:-translate-y-0.5 hover:bg-white/[0.06]"}
                  ${busy ? "border-[#D4C3A3] ring-2 ring-[#D4C3A3]/30" : ""}
                  ${i === 0 && rows.length === 0 && !scanning ? "animate-pulse" : ""}
                  disabled:cursor-not-allowed`}
              >
                <s.icon className={`h-4 w-4 ${done ? "text-emerald-400" : "text-[#D4C3A3]"}`} />
                <span className="text-xs font-bold text-zinc-200 leading-tight truncate w-full">{s.vendor.split(" ")[0]}</span>
                <span className="text-[11px] font-semibold text-zinc-500 font-mono-num">${s.amount.toFixed(2)}</span>
                {done && <span className="absolute top-2 right-2 text-[9px] font-bold text-emerald-400">✓ sorted</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
